import { LandingCohLogo } from "@/assets/LandingCohLogo";
import { useEffect, useState, FC } from "react";
import { useTranslation } from "react-i18next";
import { ImpactItemType } from "@/types/landing-page";
import { Skeleton } from "@/components/ui/skeleton";

interface ImpactToDateProps {
  isContentLoading: boolean;
  content?: ImpactItemType[];
}

const ImpactToDate: FC<ImpactToDateProps> = ({ isContentLoading, content }) => {
  const { t } = useTranslation();

  // Create a mixed array of API data and static images
  const impactsData: {
    type: "image" | "stats";
    src?: string;
    title?: string;
    description?: string;
    icon?: JSX.Element | null;
    bgColor?: string;
  }[] = [];

  if (content) {
    content.forEach((item, index) => {
      if (item.image) {
        // Use image from API
        impactsData.push({
          type: "image",
          src: item.image.medium_url || item.image.minio_url,
        });
      } else if (item.title && item.description) {
        // Use stats from API
        impactsData.push({
          type: "stats",
          title: item.title,
          description: item.description,
          icon: index % 2 === 0 ? <LandingCohLogo /> : null,
          bgColor: index % 3 === 0 ? "border border-[#E2DDD7]" : "bg-[#E2DDD7]",
        });
      }
    });
  }

  const [isMediumScreen, setIsMediumScreen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMediumScreen(window.innerWidth >= 768);
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return (
    <div className="mb-8 mt-8 px-6 md:mb-24 md:mt-24">
      <h2 className="text-2xl font-650 leading-[127%] md:text-5xl md:leading-[120%]">
        {t("landingPage.impact")}
      </h2>
      <div className="my-4 grid grid-cols-2 gap-4 md:grid-cols-4">
        {isContentLoading
          ? // Show loading skeletons
            Array.from({ length: 8 }, (_, index) => (
              <div key={`skeleton-${index}`} className="h-[186px] w-full rounded-[16px] p-4">
                <Skeleton className="h-full w-full rounded-[16px]" />
              </div>
            ))
          : impactsData.map((impact: any, index) => {
              if (impact.type === "image" && !isMediumScreen) {
                return null;
              }

              return (
                <div
                  key={`impact-${impact.type}-${index}`}
                  className={`h-[186px] w-full rounded-[16px] ${
                    impact.type === "stats" ? impact.bgColor || "bg-[#E2DDD7]" : ""
                  } relative flex flex-col justify-end ${impact.type === "image" ? "" : "p-4 md:p-6"}`}
                >
                  {impact.type === "image" ? (
                    <img
                      className="h-full w-full rounded-[16px] object-cover object-center"
                      src={impact.src}
                      alt="Impact illustration"
                    />
                  ) : (
                    <>
                      {impact.icon}
                      <h1 className="mb-2 text-2xl font-650 leading-[120%] md:text-5xl">
                        {impact.title}
                      </h1>
                      <p className="text-base font-350 leading-[120%] md:text-lg md:leading-[140%]">
                        {impact.description}
                      </p>
                    </>
                  )}
                </div>
              );
            })}
      </div>
    </div>
  );
};

export default ImpactToDate;
